<template>
  <div
    v-if="isAddTile"
    class="ziel-tile ziel-tile-add box"
    role="button"
    tabindex="0"
    @click="$emit('add')"
    @keydown.enter="$emit('add')"
    @keydown.space.prevent="$emit('add')"
  >
    <b-icon icon="plus" size="is-large" type="is-success" />
    <p>{{ $t('chapter7.tile.addTile') }}</p>
  </div>

  <div v-else class="ziel-tile ziel-tile-data box">
    <div class="ziel-tile-body">
      <b-tooltip
        :label="isValid ? $t('chapter7.tile.validComplete') : $t('chapter7.tile.validIncomplete')"
        :type="isValid ? 'is-success' : 'is-danger'"
      >
        <b-icon
          :icon="isValid ? 'check-circle' : 'close-circle'"
          :type="isValid ? 'is-success' : 'is-danger'"
          size="is-large"
        />
      </b-tooltip>
      <p class="ziel-tile-empfaenger">{{ ziel?.empfaenger || $t('chapter7.tile.noEmpfaenger') }}</p>
    </div>

    <div class="ziel-tile-footer">
      <b-tooltip :label="$t('general.edit')" type="is-dark">
        <button
          type="button"
          class="ziel-tile-edge ziel-tile-edge-edit"
          :aria-label="$t('general.edit')"
          @click="$emit('edit')"
        >
          <b-icon icon="pencil" size="is-small" />
        </button>
      </b-tooltip>
      <b-tooltip :label="$t('general.delete')" type="is-dark">
        <button
          type="button"
          class="ziel-tile-edge ziel-tile-edge-delete"
          :aria-label="$t('general.delete')"
          @click="$emit('delete')"
        >
          <b-icon icon="delete" size="is-small" />
        </button>
      </b-tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BIcon, BTooltip } from 'buefy'
import type { DatenUebertragungsZiel } from '@/types/studyTypes'

defineProps<{
  ziel?: DatenUebertragungsZiel
  isValid?: boolean
  isAddTile?: boolean
}>()

defineEmits<{ add: []; edit: []; delete: [] }>()
</script>

<style scoped>
.ziel-tile {
  width: 220px;
  height: 195px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  text-align: center;
}

.ziel-tile-add {
  cursor: pointer;
  border: 1px dashed hsl(0, 0%, 71%);
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.ziel-tile-add:hover,
.ziel-tile-add:focus {
  border-color: hsl(141, 53%, 53%);
}

.ziel-tile-data {
  padding: 0;
}

.ziel-tile-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
}

.ziel-tile-empfaenger {
  font-weight: 600;
  overflow-wrap: anywhere;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.ziel-tile-footer {
  display: flex;
  flex-shrink: 0;
  border-top: 1px solid var(--bulma-border-weak);
}

/* b-tooltip wraps each button in ".b-tooltip > .tooltip-trigger" divs;
   those, not the button, are the actual flex children of the footer, so
   the whole chain needs to stretch for the buttons to fill the footer. */
.ziel-tile-footer > .b-tooltip {
  flex: 1;
  display: flex;
}

.ziel-tile-footer > .b-tooltip + .b-tooltip {
  border-left: 1px solid var(--bulma-border-weak);
}

.ziel-tile-footer :deep(.tooltip-trigger) {
  display: flex;
  flex: 1;
}

.ziel-tile-edge {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: inherit;
}

/* Match the box's own rounded corners now that .ziel-tile no longer clips
   overflow (clipping also clipped tooltip popups poking above the footer). */
.ziel-tile-footer > .b-tooltip:first-child .ziel-tile-edge {
  border-bottom-left-radius: var(--bulma-radius-large);
}

.ziel-tile-footer > .b-tooltip:last-child .ziel-tile-edge {
  border-bottom-right-radius: var(--bulma-radius-large);
}

.ziel-tile-edge :deep(.icon) {
  margin: 0 !important;
}

.ziel-tile-edge-edit:hover {
  background: hsla(var(--bulma-info-h), var(--bulma-info-s), var(--bulma-info-l), 0.15);
  color: var(--bulma-info);
}

.ziel-tile-edge-delete {
  background: hsla(var(--bulma-danger-h), var(--bulma-danger-s), var(--bulma-danger-l), 0.15);
  color: var(--bulma-danger-dark);
}

.ziel-tile-edge-delete:hover {
  background: hsla(var(--bulma-danger-h), var(--bulma-danger-s), var(--bulma-danger-l), 0.3);
}
</style>
